import {
  CARRIERS,
  type Carrier,
} from "@shared-core/data/supplier-portal-adapter";
import {
  FORM_WORDING,
  PICK_WORDING,
  carrierLabel,
} from "@shared-core/domain/supplier-portal-wording";
import { Input } from "../../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import { FormField } from "../../app/components/patterns/FormField";
import type { DespatchFields } from "./despatchFields";

/**
 * The inputs every despatch needs, whether it covers one pick or many: the warehouse's despatch reference, the date,
 * the carrier (with its name for "other") and the tracking number (not for our own van). A refusal's field errors
 * sit under their inputs; `idPrefix` keeps the ids of two forms on one page apart.
 */
export function PickDespatchFields({
  idPrefix,
  value,
  onChange,
  errors,
  today,
}: {
  idPrefix: string;
  value: DespatchFields;
  onChange: (next: DespatchFields) => void;
  errors: Record<string, string>;
  today: string;
}) {
  const set = (patch: Partial<DespatchFields>) =>
    onChange({ ...value, ...patch });
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <FormField
        id={`${idPrefix}-ref`}
        label={PICK_WORDING.despatchRef}
        hint={PICK_WORDING.despatchRefHint}
        error={errors.despatch_ref}
      >
        {(control) => (
          <Input
            {...control}
            value={value.ref}
            autoComplete="off"
            onChange={(event) => set({ ref: event.target.value })}
          />
        )}
      </FormField>
      <FormField
        id={`${idPrefix}-date`}
        label={PICK_WORDING.despatchedOn}
        error={errors.despatched_on}
      >
        {(control) => (
          <Input
            {...control}
            type="date"
            max={today}
            value={value.date}
            onChange={(event) => set({ date: event.target.value })}
          />
        )}
      </FormField>
      <FormField
        id={`${idPrefix}-carrier`}
        label={FORM_WORDING.carrier}
        error={errors.carrier}
      >
        {(control) => (
          <Select
            value={value.carrier}
            onValueChange={(carrier) => set({ carrier: carrier as Carrier })}
          >
            <SelectTrigger {...control} className="w-full">
              <SelectValue placeholder={FORM_WORDING.chooseCarrier} />
            </SelectTrigger>
            <SelectContent>
              {CARRIERS.map((c) => (
                <SelectItem key={c} value={c}>
                  {carrierLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FormField>
      {value.carrier === "other" && (
        <FormField
          id={`${idPrefix}-carrier-name`}
          label={FORM_WORDING.carrierName}
          error={errors.carrier_name}
        >
          {(control) => (
            <Input
              {...control}
              value={value.carrierName}
              onChange={(event) => set({ carrierName: event.target.value })}
            />
          )}
        </FormField>
      )}
      {value.carrier !== "own_van" && value.carrier !== "" && (
        <FormField
          id={`${idPrefix}-tracking`}
          label={`${FORM_WORDING.trackingNumber} (optional)`}
          error={errors.tracking_number}
        >
          {(control) => (
            <Input
              {...control}
              value={value.tracking}
              autoComplete="off"
              onChange={(event) => set({ tracking: event.target.value })}
            />
          )}
        </FormField>
      )}
    </div>
  );
}
